import { useEffect, useState, type FormEvent } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router';
import { ArrowRight, Search, Store, Ticket } from 'lucide-react';
import {
  describeError,
  getTenantSummary,
  lookupOrderByTrackingCode,
  searchTenantOrders,
  type OrderLookupMatch,
  type PagedResult,
  type PlatformOrderListItem,
  type TenantSummary,
} from '@/lib/api';
import { Badge } from '@/components/ui';
import { TenantSelector } from '@/components/TenantSelector';
import { orderBadge } from '@/components/badge-labels';
import { CardTable, THead, TBody, Tr, Th, Td } from '@/components/CardTable';
import { useDebouncedValue } from '@/lib/use-debounced-value';
import { addDays, operationalDate } from '@/lib/operational-day';

/**
 * Busca de pedidos do backoffice.
 *
 * Dois modos, porque o suporte chega ao pedido por dois caminhos diferentes:
 * o cliente tem o código de rastreio (e o operador não sabe o tenant), ou o
 * operador sabe o restaurante e precisa varrer os pedidos dele. A versão
 * anterior exigia colar dois UUIDs — o do pedido o cliente nunca tem.
 *
 * O estado vive na querystring: o operador abre o pedido, volta, e os filtros
 * continuam lá; e o link pode ser passado para um colega.
 */

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Pausa na digitação antes de o termo ir à API (cada busca é auditada). */
const SEARCH_DEBOUNCE_MS = 400;

/** Termo mais curto que vai à API; abaixo disso, nenhuma busca. */
const MIN_SEARCH_TERM_LENGTH = 3;

type Mode = 'code' | 'tenant';

const STATUS_OPTIONS = [
  { value: '', label: 'Todos os status' },
  { value: 'Pending', label: 'Pendente' },
  { value: 'Preparing', label: 'Em preparo' },
  { value: 'ReadyForPickup', label: 'Pronto para retirada' },
  { value: 'OutForDelivery', label: 'Em entrega' },
  { value: 'Completed', label: 'Concluído' },
  { value: 'Cancelled', label: 'Cancelado' },
];

const DELIVERY_OPTIONS = [
  { value: '', label: 'Entrega e retirada' },
  { value: 'Delivery', label: 'Entrega' },
  { value: 'Pickup', label: 'Retirada' },
];

/**
 * Origem levada ao detalhe do pedido, para que "voltar" devolva o operador à
 * busca que ele montou — e não à ficha do tenant.
 */
function useBackState(): { from: string } {
  const location = useLocation();
  return { from: `${location.pathname}${location.search}` };
}

/**
 * Data dos atalhos de período, no dia operacional da loja (vira às 03:00 de Brasília), que é o
 * calendário de `from`/`to` na API e no histórico do painel. A data UTC de antes já mostrava
 * amanhã depois das 21:00.
 */
function operationalDaysAgo(daysAgo: number): string {
  return addDays(operationalDate(new Date()), -daysAgo);
}

function deliveryLabel(method: string): string {
  return method.toLowerCase() === 'delivery' ? 'Entrega' : 'Retirada';
}

export function OrderSearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const mode: Mode = searchParams.get('mode') === 'tenant' ? 'tenant' : 'code';

  function patchParams(patch: Record<string, string | null>) {
    const next = new URLSearchParams(searchParams);
    for (const [key, value] of Object.entries(patch)) {
      if (value === null || value === '') next.delete(key);
      else next.set(key, value);
    }
    setSearchParams(next, { replace: true });
  }

  return (
    <div className="page page-detail" style={{ gap: 18 }}>
      <div role="tablist" aria-label="Como buscar o pedido" className="btn-row">
        <button
          type="button"
          role="tab"
          aria-selected={mode === 'code'}
          className={mode === 'code' ? 'btn btn-sm' : 'btn btn-secondary btn-sm'}
          onClick={() => patchParams({ mode: null })}
        >
          <Ticket size={16} /> Por código
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={mode === 'tenant'}
          className={mode === 'tenant' ? 'btn btn-sm' : 'btn btn-secondary btn-sm'}
          onClick={() => patchParams({ mode: 'tenant' })}
        >
          <Store size={16} /> Por restaurante
        </button>
      </div>

      {mode === 'code'
        ? <SearchByCode initialCode={searchParams.get('code') ?? ''} onCodeChange={(code) => patchParams({ code })} />
        : <SearchByTenant searchParams={searchParams} patchParams={patchParams} />}
    </div>
  );
}

// ---------------------------------------------------------------- por código

function SearchByCode({
  initialCode,
  onCodeChange,
}: {
  initialCode: string;
  onCodeChange: (code: string) => void;
}) {
  const navigate = useNavigate();
  const backState = useBackState();
  const [code, setCode] = useState(initialCode);
  // `searched` separa "ainda não busquei" de "busquei e não achei" — sem isso o
  // estado vazio apareceria antes da primeira busca.
  const [searched, setSearched] = useState(false);
  const [match, setMatch] = useState<OrderLookupMatch | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    const term = code.trim();
    if (!term) return;

    onCodeChange(term);
    setLoading(true);
    setError(null);
    setMatch(null);
    setSearched(false);
    try {
      setMatch(await lookupOrderByTrackingCode(term));
      setSearched(true);
    } catch (err) {
      setError(describeError(err));
    } finally {
      setLoading(false);
    }
  }

  return (
    <>
      <form className="card" onSubmit={onSubmit}>
        <div className="card-title">Buscar pelo código do pedido</div>
        <div className="card-subtitle" style={{ marginBottom: 16 }}>
          É o código que o cliente tem em mãos, no formato <span className="mono">VPT-XXXXXX</span>.
          Não é preciso saber o restaurante — ele é identificado na busca.
        </div>
        <div className="grid-filter">
          <div className="field" style={{ marginBottom: 0 }}>
            <label htmlFor="trackingCode">Código de rastreio</label>
            <input
              id="trackingCode"
              className="mono"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder="VPT-A1B2C3"
              autoFocus
              required
            />
          </div>
          <button type="submit" className="btn" disabled={loading || !code.trim()}>
            <Search size={18} /> {loading ? 'Buscando…' : 'Buscar'}
          </button>
        </div>
      </form>

      {error && <div className="error-banner">{error}</div>}

      <div aria-live="polite">
        {loading && <p className="muted">Localizando o pedido…</p>}

        {!loading && searched && match === null && (
          <div className="card">
            <p className="muted" style={{ margin: 0 }}>
              Nenhum pedido com o código <span className="mono">{code.trim()}</span>.
              Confira se o código está completo — a busca exige o código exato.
            </p>
          </div>
        )}

        {!loading && match && (
          <div
            className="card"
            style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}
          >
            <div>
              <div style={{ fontWeight: 700, fontSize: 16 }}>{match.tenantTradeName}</div>
              <div className="muted" style={{ fontSize: 13, marginTop: 4 }}>
                <span className="mono">{match.tenantSlug}</span>
                {' · pedido '}
                <span className="mono">{match.trackingCode}</span>
                {' · '}
                {new Date(match.createdAt).toLocaleString('pt-BR')}
              </div>
              <div style={{ marginTop: 8 }}>
                {(() => {
                  const status = orderBadge(match.status);
                  return <Badge tone={status.tone} dot>{status.label}</Badge>;
                })()}
              </div>
            </div>
            <button
              type="button"
              className="btn"
              onClick={() => navigate(
                `/tenants/${match.tenantId}/orders/${match.orderId}`,
                { state: backState },
              )}
            >
              Abrir pedido <ArrowRight size={16} />
            </button>
          </div>
        )}
      </div>
    </>
  );
}

// ----------------------------------------------------------- por restaurante

function SearchByTenant({
  searchParams,
  patchParams,
}: {
  searchParams: URLSearchParams;
  patchParams: (patch: Record<string, string | null>) => void;
}) {
  const navigate = useNavigate();
  const backState = useBackState();
  const tenantId = searchParams.get('tenantId') ?? '';
  const q = searchParams.get('q') ?? '';
  const status = searchParams.get('status') ?? '';
  const deliveryMethod = searchParams.get('deliveryMethod') ?? '';
  const from = searchParams.get('from') ?? '';
  const to = searchParams.get('to') ?? '';
  const page = Number(searchParams.get('page') ?? '1');

  const [fetchedTenant, setFetchedTenant] = useState<TenantSummary | null>(null);
  const [loadedResult, setLoadedResult] = useState<PagedResult<PlatformOrderListItem> | null>(null);
  const [loadedKey, setLoadedKey] = useState<string | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  // O tenantId pode chegar pela URL (atalho vindo do detalhe do tenant), então
  // o nome é resolvido pelo id em vez de depender da seleção na tela. O que a
  // tela mostra e derivado: o tenant buscado so vale enquanto for o do id
  // atual — sem isso, trocar de tenant piscava o nome antigo.
  const tenant = tenantId && fetchedTenant?.id === tenantId ? fetchedTenant : null;

  useEffect(() => {
    if (!tenantId || fetchedTenant?.id === tenantId) return;
    let cancelled = false;
    void getTenantSummary(tenantId).then((t) => { if (!cancelled) setFetchedTenant(t); });
    return () => { cancelled = true; };
  }, [tenantId, fetchedTenant?.id]);

  // O termo só vai à API depois de uma pausa na digitação e com pelo menos 3 caracteres: cada
  // busca grava `order.searched`, e a busca tecla a tecla deixaria na trilha um prefixo por
  // dígito do telefone (tarefa 8.3 de platform-backoffice-hardening, revisão E16).
  const debouncedQ = useDebouncedValue(q, SEARCH_DEBOUNCE_MS);
  const termLength = debouncedQ.trim().length;
  const termTooShort = termLength > 0 && termLength < MIN_SEARCH_TERM_LENGTH;
  const searchable = Boolean(tenantId) && !termTooShort;

  // Mesma ideia para a busca: `loading`, `error` e `result` saem da comparacao
  // entre a busca que terminou e a que os filtros pedem agora.
  const requestKey = searchable ? `${tenantId}|${debouncedQ}|${status}|${deliveryMethod}|${from}|${to}|${page}` : '';
  const isCurrent = loadedKey === requestKey;
  const loading = searchable && !isCurrent;
  const error = isCurrent ? loadError : null;
  const result = isCurrent ? loadedResult : null;

  useEffect(() => {
    if (!searchable) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await searchTenantOrders(tenantId, { q: debouncedQ, status, deliveryMethod, from, to, page });
        if (cancelled) return;
        setLoadedResult(r);
        setLoadError(null);
      } catch (err) {
        if (cancelled) return;
        setLoadError(describeError(err));
        setLoadedResult(null);
      } finally {
        if (!cancelled) setLoadedKey(requestKey);
      }
    })();
    return () => { cancelled = true; };
  }, [searchable, tenantId, debouncedQ, status, deliveryMethod, from, to, page, requestKey]);

  // Colar o UUID do pedido continua abrindo direto: quem já tem o id em mãos
  // não deveria perder o atalho antigo.
  const pastedOrderId = UUID_PATTERN.test(q.trim()) ? q.trim() : null;

  return (
    <>
      <div className="card">
        <div className="field">
          <label htmlFor="tenant">Restaurante</label>
          <TenantSelector
            id="tenant"
            value={tenant}
            onChange={(t) => {
              setFetchedTenant(t);
              patchParams({ tenantId: t?.id ?? null, page: null });
            }}
          />
        </div>

        {tenantId && (
          <>
            <div className="grid-3" style={{ marginTop: 14 }}>
              <div className="field" style={{ marginBottom: 0 }}>
                <label htmlFor="q">Código, nome ou telefone do cliente</label>
                <input
                  id="q"
                  value={q}
                  placeholder="VPT-A1B2C3, Maria, 11999998888…"
                  onChange={(e) => patchParams({ q: e.target.value, page: null })}
                />
              </div>
              <div className="field" style={{ marginBottom: 0 }}>
                <label htmlFor="status">Status</label>
                <select
                  id="status"
                  value={status}
                  onChange={(e) => patchParams({ status: e.target.value, page: null })}
                >
                  {STATUS_OPTIONS.map((s) => (
                    <option key={s.value} value={s.value}>{s.label}</option>
                  ))}
                </select>
              </div>
              <div className="field" style={{ marginBottom: 0 }}>
                <label htmlFor="delivery">Entrega</label>
                <select
                  id="delivery"
                  value={deliveryMethod}
                  onChange={(e) => patchParams({ deliveryMethod: e.target.value, page: null })}
                >
                  {DELIVERY_OPTIONS.map((d) => (
                    <option key={d.value} value={d.value}>{d.label}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="grid-3" style={{ marginTop: 14, alignItems: 'end' }}>
              <div className="field" style={{ marginBottom: 0 }}>
                <label htmlFor="from">De</label>
                <input
                  id="from"
                  type="date"
                  value={from}
                  onChange={(e) => patchParams({ from: e.target.value, page: null })}
                />
              </div>
              <div className="field" style={{ marginBottom: 0 }}>
                <label htmlFor="to">Até</label>
                <input
                  id="to"
                  type="date"
                  value={to}
                  onChange={(e) => patchParams({ to: e.target.value, page: null })}
                />
              </div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <button type="button" className="btn btn-secondary btn-sm"
                  onClick={() => patchParams({ from: operationalDaysAgo(0), to: operationalDaysAgo(0), page: null })}>
                  Hoje
                </button>
                <button type="button" className="btn btn-secondary btn-sm"
                  onClick={() => patchParams({ from: operationalDaysAgo(7), to: operationalDaysAgo(0), page: null })}>
                  7 dias
                </button>
                <button type="button" className="btn btn-secondary btn-sm"
                  onClick={() => patchParams({ from: operationalDaysAgo(30), to: operationalDaysAgo(0), page: null })}>
                  30 dias
                </button>
                <button type="button" className="btn btn-secondary btn-sm"
                  onClick={() => patchParams({ from: null, to: null, page: null })}>
                  Limpar período
                </button>
              </div>
            </div>

            {pastedOrderId && (
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                style={{ marginTop: 12 }}
                onClick={() => navigate(
                  `/tenants/${tenantId}/orders/${pastedOrderId}`,
                  { state: backState },
                )}
              >
                Abrir pedido pelo ID colado: <span className="mono">{pastedOrderId}</span>
              </button>
            )}
          </>
        )}
      </div>

      {error && <div className="error-banner">{error}</div>}

      {!tenantId && (
        <div className="card">
          <p className="muted" style={{ margin: 0 }}>
            Escolha um restaurante para ver os pedidos dele.
          </p>
        </div>
      )}

      {tenantId && (
        <div aria-live="polite">
          <div className="card card-flush">
            <div className="table-scroll">
              <CardTable>
                <THead>
                  <Tr>
                    <Th>Código</Th>
                    <Th>Cliente</Th>
                    <Th>Status</Th>
                    {/* "Tipo" e não "Entrega": a coluna cujo valor é
                        Entrega/Retirada não pode se chamar Entrega. */}
                    <Th>Tipo</Th>
                    <Th style={{ textAlign: 'right' }}>Total</Th>
                    <Th>Criado em</Th>
                    <Th></Th>
                  </Tr>
                </THead>
                <TBody>
                  {termTooShort && (
                    <Tr>
                      <Td colSpan={7} style={{ textAlign: 'center', padding: 24 }} className="muted">
                        Digite pelo menos {MIN_SEARCH_TERM_LENGTH} caracteres para buscar.
                      </Td>
                    </Tr>
                  )}
                  {loading && (
                    <Tr><Td colSpan={7} style={{ textAlign: 'center', padding: 24 }} className="muted">Carregando…</Td></Tr>
                  )}
                  {!loading && result?.items.length === 0 && (
                    <Tr>
                      <Td colSpan={7} style={{ textAlign: 'center', padding: 24 }} className="muted">
                        Nenhum pedido encontrado com estes filtros.
                      </Td>
                    </Tr>
                  )}
                  {!loading && result?.items.map((o) => {
                    const badge = orderBadge(o.status);
                    return (
                      <Tr key={o.id}>
                        <Td heading className="mono" style={{ fontWeight: 600 }}>{o.trackingCode}</Td>
                        <Td label="Cliente">
                          <div>{o.customerName}</div>
                          <div className="muted" style={{ fontSize: 12 }}>{o.customerPhone}</div>
                        </Td>
                        <Td label="Status">
                          <Badge tone={badge.tone} dot>{badge.label}</Badge>
                          {o.operationallyPurgedAt && <Badge tone="warning">Purgado</Badge>}
                        </Td>
                        <Td label="Tipo" className="muted">{deliveryLabel(o.deliveryMethod)}</Td>
                        <Td label="Total" style={{ textAlign: 'right', fontWeight: 600 }}>R$ {o.total.toFixed(2)}</Td>
                        <Td label="Criado em" className="muted">{new Date(o.createdAt).toLocaleString('pt-BR')}</Td>
                        <Td action style={{ textAlign: 'right' }}>
                          <Link
                            to={`/tenants/${tenantId}/orders/${o.id}`}
                            state={backState}
                            style={{ fontWeight: 600 }}
                          >
                            Detalhe →
                          </Link>
                        </Td>
                      </Tr>
                    );
                  })}
                </TBody>
              </CardTable>
            </div>
          </div>

          {result && result.totalPages > 1 && (
            <div className="pagination">
              <span className="pagination-info">
                {result.totalItems} {result.totalItems === 1 ? 'pedido' : 'pedidos'}
                {' · página '}{result.page} de {result.totalPages}
              </span>
              <button
                className="btn btn-secondary btn-sm"
                onClick={() => patchParams({ page: String(Math.max(1, page - 1)) })}
                disabled={page <= 1 || loading}
              >Anterior</button>
              <button
                className="btn btn-secondary btn-sm"
                onClick={() => patchParams({ page: String(Math.min(result.totalPages, page + 1)) })}
                disabled={page >= result.totalPages || loading}
              >Próxima</button>
            </div>
          )}
        </div>
      )}
    </>
  );
}
